import * as React from "react"

import { useMediaQuery } from "@/hooks/useMediaQuery"
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Drawer,
  DrawerBody,
  DrawerClose,
  DrawerContent,
  DrawerDescription,
  DrawerFooter,
  DrawerHeader,
  DrawerTitle,
} from "@/components/ui/drawer"
import { cn } from "@/lib/utils"

/**
 * ResponsiveDialog – one API, two surfaces.
 *   < md (768px): bottom Drawer (vaul) with handle, optional snap points, safe-area footer.
 *   >= md:        centred Dialog (radix).
 *
 * Structure (same on both):
 *   <ResponsiveDialog open onOpenChange snapPoints={[0.6, 0.94]}>
 *     <ResponsiveDialogContent>
 *       <ResponsiveDialogHeader><ResponsiveDialogTitle/><ResponsiveDialogDescription/></ResponsiveDialogHeader>
 *       <ResponsiveDialogBody> …scrollable content… </ResponsiveDialogBody>
 *       <ResponsiveDialogFooter> …buttons… </ResponsiveDialogFooter>
 *     </ResponsiveDialogContent>
 *   </ResponsiveDialog>
 *
 * Forms: wrap Body + Footer in <form className="contents"> so the drawer flex layout still applies.
 */
const ResponsiveContext = React.createContext({ desktop: true, atTop: true, snapPoints: null })
const useIsDesktop = () => useMediaQuery("(min-width: 768px)")
const useResponsiveDialog = () => React.useContext(ResponsiveContext)

const ResponsiveDialog = ({ children, snapPoints, defaultSnapIndex = 0, drawerProps, ...props }) => {
  const desktop = useIsDesktop()
  const initial = snapPoints ? snapPoints[Math.min(defaultSnapIndex, snapPoints.length - 1)] : null
  const [snap, setSnap] = React.useState(initial)

  // Reset to the default snap point every time the drawer opens
  React.useEffect(() => {
    if (props.open && snapPoints) setSnap(initial)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.open])

  const last = snapPoints ? snapPoints[snapPoints.length - 1] : null
  const atTop = !snapPoints || snap === last
  // Distance (in dvh units) between the current snap point and the fully-open one.
  // The footer is pushed up by this amount so actions stay visible at every snap point.
  const snapOffset = snapPoints && typeof snap === "number" ? Math.max(0, (last - snap) * 100) : 0
  const ctx = React.useMemo(() => ({ desktop, atTop, snapPoints, snap, snapOffset }), [desktop, atTop, snapPoints, snap, snapOffset])

  if (desktop) {
    return (
      <ResponsiveContext.Provider value={ctx}>
        <Dialog {...props}>{children}</Dialog>
      </ResponsiveContext.Provider>
    )
  }
  const snapProps = snapPoints
    ? { snapPoints, activeSnapPoint: snap, setActiveSnapPoint: setSnap, fadeFromIndex: 0 }
    : {}
  return (
    <ResponsiveContext.Provider value={ctx}>
      <Drawer {...snapProps} {...drawerProps} {...props}>
        {children}
      </Drawer>
    </ResponsiveContext.Provider>
  )
}

const ResponsiveDialogContent = React.forwardRef(({ className, children, drawerClassName, ...props }, ref) => {
  const { desktop, snapPoints } = useResponsiveDialog()
  if (desktop) {
    return (
      <DialogContent ref={ref} className={className} data-testid="responsive-dialog" {...props}>
        {children}
      </DialogContent>
    )
  }
  const last = snapPoints ? snapPoints[snapPoints.length - 1] : null
  return (
    <DrawerContent
      ref={ref}
      className={cn(snapPoints && "max-h-none", drawerClassName)}
      style={typeof last === "number" ? { height: `calc(${last} * 100dvh)` } : undefined}
      data-testid="responsive-dialog"
      data-surface="drawer"
      {...props}
    >
      {children}
    </DrawerContent>
  )
})
ResponsiveDialogContent.displayName = "ResponsiveDialogContent"

const ResponsiveDialogHeader = ({ className, ...props }) => {
  const { desktop } = useResponsiveDialog()
  return desktop
    ? <DialogHeader className={className} {...props} />
    : <DrawerHeader className={cn("shrink-0", className)} {...props} />
}

/** Scrollable middle section. Desktop: plain grid. Mobile: DrawerBody (scrolls only at top snap point). */
const ResponsiveDialogBody = ({ className, ...props }) => {
  const { desktop, atTop } = useResponsiveDialog()
  // minmax(0,1fr) + min-w-0 children: long/truncated text must never widen the surface
  if (desktop) return <div className={cn("grid grid-cols-[minmax(0,1fr)] gap-4 [&>*]:min-w-0", className)} {...props} />
  return <DrawerBody className={cn("grid grid-cols-[minmax(0,1fr)] content-start gap-4 pt-1 [&>*]:min-w-0", !atTop && "overflow-hidden", className)} {...props} />
}

const ResponsiveDialogFooter = ({ className, style, ...props }) => {
  const { desktop, snapPoints, snapOffset } = useResponsiveDialog()
  if (desktop) return <DialogFooter className={className} style={style} {...props} />
  const snapStyle = snapPoints
    ? { marginBottom: `calc(${snapOffset} * 1dvh)`, transition: "margin-bottom 0.5s cubic-bezier(0.32, 0.72, 0, 1)", ...style }
    : style
  return <DrawerFooter className={cn("shrink-0 [&>button]:h-12 [&>button]:w-full [&>a]:w-full", className)} style={snapStyle} {...props} />
}

const ResponsiveDialogTitle = React.forwardRef(({ className, ...props }, ref) => {
  const { desktop } = useResponsiveDialog()
  return desktop ? <DialogTitle ref={ref} className={className} {...props} /> : <DrawerTitle ref={ref} className={className} {...props} />
})
ResponsiveDialogTitle.displayName = "ResponsiveDialogTitle"

const ResponsiveDialogDescription = React.forwardRef(({ className, ...props }, ref) => {
  const { desktop } = useResponsiveDialog()
  return desktop ? <DialogDescription ref={ref} className={className} {...props} /> : <DrawerDescription ref={ref} className={className} {...props} />
})
ResponsiveDialogDescription.displayName = "ResponsiveDialogDescription"

const ResponsiveDialogClose = React.forwardRef((props, ref) => {
  const { desktop } = useResponsiveDialog()
  return desktop ? <DialogClose ref={ref} {...props} /> : <DrawerClose ref={ref} {...props} />
})
ResponsiveDialogClose.displayName = "ResponsiveDialogClose"

export {
  ResponsiveDialog,
  ResponsiveDialogContent,
  ResponsiveDialogHeader,
  ResponsiveDialogBody,
  ResponsiveDialogFooter,
  ResponsiveDialogTitle,
  ResponsiveDialogDescription,
  ResponsiveDialogClose,
  useIsDesktop,
  useResponsiveDialog,
}
